// src/helpers/rutaProductos.js
import axios from "axios";

const RAW_BASE_URL =
  process.env.REACT_APP_API_BASE || "https://backre-miami.us-3.evennode.com";

const BASE_URL = String(RAW_BASE_URL).replace(/\/+$/, "");

const API = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
});

const p = (path) => `/${String(path || "").replace(/^\/+/, "")}`;

const getToken = () => localStorage.getItem("token") || "";

const auth = () => {
  const token = getToken();
  return token ? { headers: { token } } : {};
};

const debug =
  String(process.env.REACT_APP_DEBUG_API || "").toLowerCase() === "true";

const logUrl = (url, params, data, method = "GET") => {
  if (!debug) return;

  console.log("👉 METHOD:", method);
  console.log("👉 URL:", API.getUri({ url, params }));

  if (data) {
    console.log("📦 DATA ENVIADA:", data);
  }
};

async function req(method, url, { params, data, signal } = {}) {
  logUrl(url, params, data, method);

  try {
    const res = await API.request({
      method,
      url,
      params,
      data,
      signal,
      ...auth(),
    });

    return res.data;
  } catch (err) {
    if (debug) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message;

      console.log("🧯 API ERROR:", msg);
      console.log("🧯 STATUS:", err?.response?.status);
      console.log("🧯 DATA:", err?.response?.data);
    }

    throw err;
  }
}

/* ----------------------------- NORMALIZADORES ---------------------------- */

const clean = (v) => String(v ?? "").trim();

const toNumber = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};

const toArray = (v) => {
  if (Array.isArray(v)) return v;
  if (v === null || v === undefined || v === "") return [];
  return [v];
};

const unwrapProducto = (data) => {
  if (!data) return null;
  if (data?.producto) return data.producto;
  if (data?.data) return data.data;
  return data;
};

const unwrapProductos = (data) => {
  if (Array.isArray(data)) return data;

  if (Array.isArray(data?.productos)) return data.productos;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.docs)) return data.docs;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.items)) return data.items;

  return [];
};

export const normalizarProductoPayload = (payload = {}) => {
  const src = payload || {};

  const usaColor = src.usaColor ?? false;

  return {
    ...src,

    codprod: src.codprod ? Number(src.codprod) : undefined,

    categoria: clean(src.categoria),
    marca: clean(src.marca),

    modelo: clean(src.modelo).toUpperCase(),
    nombreComercial: clean(src.nombreComercial).toUpperCase(),
    descripcion: clean(src.descripcion).toUpperCase(),

    tipoMotor: clean(src.tipoMotor || "NO_APLICA").toUpperCase(),

    cilindradaCC: toNumber(src.cilindradaCC, 0),
    potenciaWatts: toNumber(src.potenciaWatts, 0),
    potenciaKw: toNumber(src.potenciaKw, 0),
    tensionVolt: toNumber(src.tensionVolt, 0),
    capacidadAh: toNumber(src.capacidadAh, 0),
    capacidadKwh: toNumber(src.capacidadKwh, 0),
    cantidadPaneles: toNumber(src.cantidadPaneles, 0),
    potenciaPanelWatts: toNumber(src.potenciaPanelWatts, 0),

    usaColor,
    coloresDisponibles: usaColor ? toArray(src.coloresDisponibles) : [],

    precioVentaUSD: toNumber(src.precioVentaUSD, 0),
    precioMinimoUSD: toNumber(src.precioMinimoUSD, 0),

    ...(Object.prototype.hasOwnProperty.call(
      src,
      "costoProductoUSD"
    )
      ? {
          costoProductoUSD: toNumber(
            src.costoProductoUSD,
            0
          ),
        }
      : {}),
    
    comisionBaseUSD: toNumber(src.comisionBaseUSD, 0),
    costoEnvioUSD: toNumber(src.costoEnvioUSD, 0),

    politicaEnvio: clean(src.politicaEnvio || "NO_APLICA").toUpperCase(),
    permiteEnvio: src.permiteEnvio ?? true,
    demoraEntrega: clean(src.demoraEntrega).toUpperCase(),
    aclaracion: clean(src.aclaracion).toUpperCase(),

    especificaciones: Array.isArray(src.especificaciones)
      ? src.especificaciones.map((e) => ({
          nombre: clean(e.nombre).toUpperCase(),
          valor: clean(e.valor).toUpperCase(),
        }))
      : [],

    observaciones: clean(src.observaciones).toUpperCase(),
    activo: src.activo ?? true,
  };
};

/* ------------------------------- PRODUCTOS ------------------------------- */

export async function getProductos({
  page = 1,
  limit = 50,
  q = "",
  categoria = "",
  marca = "",
  estado = "activos",
  signal,
} = {}) {
  const params = {
    page,
    limit,
    q: String(q || "").trim(),
    estado,
  };

  if (categoria) params.categoria = categoria;
  if (marca) params.marca = marca;

  const data = await req("GET", p("productos"), {
    params,
    signal,
  });

  const productos = unwrapProductos(data);

  return {
    productos,
    total: Number(data?.total ?? productos.length),
    page: Number(data?.page ?? page),
    limit: Number(data?.limit ?? limit),
  };
}

export async function getProductoById(id, { signal } = {}) {
  const data = await req("GET", p(`productos/${id}`), { signal });
  return unwrapProducto(data);
}

export async function getProductoByCodigo(codprod, { signal } = {}) {
  const codigo = String(codprod || "").trim();

  if (!codigo) return null;

  const data = await req("GET", p(`productos/codigo/${codigo}`), { signal });

  return unwrapProducto(data);
}

export async function postProducto(payload, { signal } = {}) {
  const data = normalizarProductoPayload(payload);

  if (debug) {
    console.log("🟢 postProducto PAYLOAD:", data);
  }

  return req("POST", p("productos"), {
    data,
    signal,
  });
}

export async function putProducto(id, payload, { signal } = {}) {
  const data = normalizarProductoPayload(payload);

  // En edición NO se modifica codprod.
  // Evita error de validación unique / ownerDocument en Mongoose.
  delete data.codprod;
  delete data._id;
  delete data.__v;
  delete data.createdAt;
  delete data.updatedAt;

  if (debug) {
    console.log("🟣 putProducto ID:", id);
    console.log("🟣 putProducto ORIGINAL:", payload);
    console.log("🟢 putProducto NORMALIZADO SIN CODPROD:", data);
    console.log("🌐 putProducto URL:", `${BASE_URL}${p(`productos/${id}`)}`);
  }

  return req("PUT", p(`productos/${id}`), {
    data,
    signal,
  });
}

export async function deleteProducto(id, { signal } = {}) {
  return req("DELETE", p(`productos/${id}`), { signal });
}

// Compatible con:
// buscarProductos({ q: "voge", limit: 20 })
// buscarProductos("voge", 20)
export async function buscarProductos(arg1 = {}, arg2 = 20) {
  let q = "";
  let limit = 20;
  let signal;

  if (typeof arg1 === "string" || typeof arg1 === "number") {
    q = String(arg1 || "").trim();
    limit = Number(arg2 || 20);
  } else {
    q = String(arg1?.q || "").trim();
    limit = Number(arg1?.limit || 20);
    signal = arg1?.signal;
  }

  if (!q || q.length < 2) return [];

  const data = await req("GET", p("productos/buscar"), {
    params: { q, limit },
    signal,
  });

  const productos = unwrapProductos(data);

  if (debug) {
    console.log("🔎 buscarProductos q:", q);
    console.log("🔎 productos encontrados:", productos);
  }

  return productos;
}